class BuyXGetYOffers extends HTMLElement {
  constructor() {
    super();
    this.DEBUG = false;
    this.OFFER_PROPERTY_KEY = '_Offer';
    this.offers = [];
    this.isRunning = false;
    this.runQueued = false;
    this.debounceTimer = null;

    this.handleCartChange = this.handleCartChange.bind(this);
    this.handleCartRefresh = this.handleCartRefresh.bind(this);
    this.handleDrawerOpen = this.handleDrawerOpen.bind(this);
  }

  connectedCallback() {
    this.parseConfig();
    if (!this.offers.length) return;

    this.messagesEl = this.querySelector('[data-offer-messages]');

    document.addEventListener('theme:cart:change', this.handleCartChange);
    document.addEventListener('theme:cart:refresh', this.handleCartRefresh);
    document.addEventListener('theme:cart-drawer:open', this.handleDrawerOpen);

    this.scheduleRun(null, true);
  }

  disconnectedCallback() {
    document.removeEventListener('theme:cart:change', this.handleCartChange);
    document.removeEventListener('theme:cart:refresh', this.handleCartRefresh);
    document.removeEventListener('theme:cart-drawer:open', this.handleDrawerOpen);
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  }

  parseConfig() {
    const script = this.querySelector('script[type="application/json"]');
    if (!script) return;

    try {
      const parsed = JSON.parse(script.textContent || '{}');
      const rawOffers = Array.isArray(parsed.offers) ? parsed.offers : [];

      this.offers = rawOffers
        .map((raw, index) => this.normalizeOffer(raw, index))
        .filter((offer) => offer && offer.product_x_variants.length && offer.product_y_variants.length);

      // Keep backwards compatibility for legacy carts that may have used offer_label as property value,
      // but only when that label is unique across all offers.
      const labelCounts = new Map();
      this.offers.forEach((offer) => {
        const label = String(offer.offer_label || '').trim();
        if (!label) return;
        labelCounts.set(label, (labelCounts.get(label) || 0) + 1);
      });
      this.offers.forEach((offer) => {
        const label = String(offer.offer_label || '').trim();
        offer.accepted_property_values = [offer.offer_id];
        if (label && labelCounts.get(label) === 1) {
          offer.accepted_property_values.push(label);
        }
      });
    } catch (error) {
      console.error('[BuyXGetYOffers] Failed to parse config', error);
      this.offers = [];
    }
  }

  normalizeOffer(raw, index) {
    if (!raw || typeof raw !== 'object') return null;

    const xVariants = Array.isArray(raw.product_x_variants)
      ? raw.product_x_variants.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
      : [];

    const yVariants = Array.isArray(raw.product_y_variants)
      ? raw.product_y_variants.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
      : [];

    const excludedVariants = Array.isArray(raw.excluded_variants)
      ? raw.excluded_variants.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
      : [];

    const qty = Number(raw.product_y_quantity || 1);

    const offerId = String(raw.offer_id || `buy_x_get_y_${index + 1}`);
    const offerLabel = raw.offer_label ? String(raw.offer_label) : '';

    return {
      title: String(raw.title || 'Free Gift Offer'),
      offer_id: offerId,
      offer_label: offerLabel,
      accepted_property_values: [offerId],
      product_x_variants: xVariants,
      product_y_variants: yVariants,
      product_y_quantity: Number.isFinite(qty) && qty > 0 ? qty : 1,
      only_1qty_of_y_in_cart:
        raw.only_1qty_of_y_in_cart === true || String(raw.only_1qty_of_y_in_cart).toLowerCase() === 'true',
      look_for_x_in_mix_match_bundle:
        raw.look_for_x_in_mix_match_bundle === true ||
        String(raw.look_for_x_in_mix_match_bundle).toLowerCase() === 'true',
      excluded_variant_set: new Set(excludedVariants),
      message_icon: raw.message_icon ? String(raw.message_icon) : '',
      success_message: raw.success_message ? String(raw.success_message) : 'Free gift added to your cart!',
      pre_message: raw.pre_message ? String(raw.pre_message) : '',
      post_message: raw.post_message ? String(raw.post_message) : '',
      pre_message_styles: raw.pre_message_styles || {},
      post_message_styles: raw.post_message_styles || {}
    };
  }

  handleCartChange(event) {
    this.scheduleRun(event?.detail?.cart || null, false);
  }

  handleCartRefresh(event) {
    if (event?.detail?.source === 'buy-x-get-y') return;
    this.scheduleRun(event?.detail?.cart || null, false);
  }

  handleDrawerOpen() {
    this.scheduleRun(null, false);
  }

  getCartRoot() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  scheduleRun(eventCart, forceFresh) {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.run(eventCart, forceFresh), 90);
  }

  async fetchCartDirect(forceFresh = false) {
    const root = this.getCartRoot();
    const cartUrl = forceFresh ? `${root}cart.js?_=${Date.now()}` : `${root}cart.js`;

    const response = await fetch(cartUrl, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      cache: 'no-store',
      credentials: 'same-origin'
    });

    return await response.json();
  }

  async getCart(eventCart, forceFresh = false) {
    if (eventCart && Array.isArray(eventCart.items)) return eventCart;

    if (window.CartOfferUtils && typeof window.CartOfferUtils.getCartData === 'function') {
      const cartFromUtils = await window.CartOfferUtils.getCartData(null, forceFresh);
      if (cartFromUtils && Array.isArray(cartFromUtils.items)) return cartFromUtils;
    }

    return await this.fetchCartDirect(forceFresh);
  }

  async run(eventCart, forceFresh = false) {
    if (this.isRunning) {
      this.runQueued = true;
      return;
    }

    this.isRunning = true;
    try {
      const cart = await this.getCart(eventCart, forceFresh);
      if (!cart || !Array.isArray(cart.items)) return;

      const plan = this.buildMutationPlan(cart);
      const changed = await this.applyPlan(plan);

      if (changed) {
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', {
          bubbles: true,
          detail: { source: 'buy-x-get-y' }
        }));
        return;
      }

      this.renderMessage(cart);
    } catch (error) {
      console.error('[BuyXGetYOffers] run failed', error);
    } finally {
      this.isRunning = false;
      if (this.runQueued) {
        this.runQueued = false;
        this.scheduleRun(null, true);
      }
    }
  }

  getItemCount(cart) {
    const payloadCount = Number(cart?.item_count || 0);
    if (payloadCount > 0) return payloadCount;
    return (Array.isArray(cart?.items) ? cart.items : []).reduce((sum, item) => sum + Number(item?.quantity || 0), 0);
  }

  // Bundle apps (e.g. Easy Bundles) add one parent line and hide the real
  // component variants in the _Details property: "46943390728413 : 1, 45798871007453 : 2".
  // Returns Map<variantId, qtyPerBundle> or null when the line is not a bundle.
  getBundleComponents(item) {
    const details = item?.properties?.['_Details'];
    if (!details || typeof details !== 'string') return null;

    const components = new Map();
    details.split(',').forEach((pair) => {
      const [rawId, rawQty] = pair.split(':');
      const variantId = Number(String(rawId || '').trim());
      const quantity = Number(String(rawQty || '').trim());
      if (Number.isFinite(variantId) && variantId > 0 && Number.isFinite(quantity) && quantity > 0) {
        components.set(variantId, (components.get(variantId) || 0) + quantity);
      }
    });

    return components.size ? components : null;
  }

  getXQty(cart, offer) {
    const xSet = new Set(offer.product_x_variants.map((id) => Number(id)));
    return cart.items.reduce((sum, item) => {
      // Prevent infinite loops when X and Y share a variant:
      // free Y lines carry _Offer, so they must not be counted as X.
      if (item?.properties && item.properties[this.OFFER_PROPERTY_KEY] != null) return sum;

      const lineQty = Number(item?.quantity || 0);
      let qty = 0;

      const variantId = Number(item?.variant_id || 0);
      if (xSet.has(variantId)) qty += lineQty;

      // Only offers opted in via look_for_x_in_mix_match_bundle count X
      // components hidden inside a bundle's _Details property.
      if (offer.look_for_x_in_mix_match_bundle) {
        const components = this.getBundleComponents(item);
        if (components) {
          components.forEach((componentQty, componentVariantId) => {
            if (xSet.has(componentVariantId)) qty += componentQty * lineQty;
          });
        }
      }

      return sum + qty;
    }, 0);
  }

  // Offer is suppressed while the cart contains any excluded variant
  // (e.g. no free sample when the full-size product is already in cart).
  // Bundle lines are checked through their _Details components too.
  isOfferExcluded(cart, offer) {
    const excluded = offer.excluded_variant_set;
    if (!excluded || !excluded.size) return false;

    return cart.items.some((item) => {
      if (excluded.has(Number(item?.variant_id || 0))) return true;

      const components = this.getBundleComponents(item);
      if (components) {
        for (const componentVariantId of components.keys()) {
          if (excluded.has(componentVariantId)) return true;
        }
      }

      return false;
    });
  }

  // Required quantity of each Y variant. By default it scales with the X
  // quantity; with only_1qty_of_y_in_cart the offer grants a flat
  // product_y_quantity per cart as long as any X is present.
  getRequiredYQty(xQty, offer) {
    if (xQty <= 0) return 0;
    const perY = Math.max(1, offer.product_y_quantity);
    return offer.only_1qty_of_y_in_cart ? perY : xQty * perY;
  }

  getOfferItems(cart, offer) {
    const accepted = new Set(offer.accepted_property_values);
    return cart.items.filter((item) => {
      if (!item?.properties) return false;
      const value = item.properties[this.OFFER_PROPERTY_KEY];
      if (value == null) return false;
      return accepted.has(String(value));
    });
  }

  buildMutationPlan(cart) {
    const updates = {};
    const adds = [];

    for (const offer of this.offers) {
      // Excluded offers behave as if no X were present, so existing free Y
      // lines are removed by the requiredQtyPerY <= 0 branch below.
      const xQty = this.isOfferExcluded(cart, offer) ? 0 : this.getXQty(cart, offer);
      const requiredQtyPerY = this.getRequiredYQty(xQty, offer);
      const offerItems = this.getOfferItems(cart, offer);

      const byVariant = new Map();
      offerItems.forEach((item) => {
        const vid = Number(item.variant_id || 0);
        if (!byVariant.has(vid)) byVariant.set(vid, []);
        byVariant.get(vid).push(item);
      });

      const ySet = new Set(offer.product_y_variants.map((id) => Number(id)));

      for (const item of offerItems) {
        const vid = Number(item.variant_id || 0);
        if (!ySet.has(vid) && item.key) updates[item.key] = 0;
      }

      for (const yVariant of offer.product_y_variants) {
        const rows = byVariant.get(Number(yVariant)) || [];
        const currentQty = rows.reduce((sum, row) => sum + Number(row.quantity || 0), 0);

        if (requiredQtyPerY <= 0) {
          rows.forEach((row) => {
            if (row.key) updates[row.key] = 0;
          });
          continue;
        }

        if (currentQty < requiredQtyPerY) {
          adds.push({
            variantId: Number(yVariant),
            quantity: requiredQtyPerY - currentQty,
            offerPropertyValue: offer.offer_id
          });
          continue;
        }

        if (currentQty > requiredQtyPerY) {
          let toReduce = currentQty - requiredQtyPerY;
          for (const row of rows) {
            if (toReduce <= 0) break;
            const rowQty = Number(row.quantity || 0);
            const reduceBy = Math.min(rowQty, toReduce);
            const newQty = rowQty - reduceBy;
            if (row.key) updates[row.key] = newQty;
            toReduce -= reduceBy;
          }
        }
      }
    }

    return { updates, adds };
  }

  async applyPlan(plan) {
    let changed = false;
    const root = this.getCartRoot();

    const updateKeys = Object.keys(plan.updates || {});
    if (updateKeys.length) {
      const res = await fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: plan.updates })
      });
      if (res.ok) changed = true;
    }

    const addItems = (plan.adds || [])
      .filter((add) => add.variantId && add.quantity)
      .map((add) => ({
        variantId: Number(add.variantId),
        quantity: Number(add.quantity),
        properties: { [this.OFFER_PROPERTY_KEY]: add.offerPropertyValue }
      }));

    if (addItems.length) {
      if (window.CartOfferUtils && typeof window.CartOfferUtils.addProducts === 'function') {
        const result = await window.CartOfferUtils.addProducts(addItems);
        if (result.success) changed = true;
      } else {
        for (const add of addItems) {
          const formData = new FormData();
          formData.append('id', add.variantId);
          formData.append('quantity', add.quantity);
          formData.append(`properties[${this.OFFER_PROPERTY_KEY}]`, add.properties[this.OFFER_PROPERTY_KEY]);
          const res = await fetch(root + 'cart/add.js', {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            body: formData
          });
          if (res.ok) changed = true;
        }
      }
    }

    return changed;
  }

  renderMessage(cart) {
    if (!this.messagesEl) return;

    const cartItemCount = this.getItemCount(cart);
    const selected = [];

    for (const offer of this.offers) {
      // No pre/post message while the offer is suppressed by an excluded item.
      if (this.isOfferExcluded(cart, offer)) continue;

      const xQty = this.getXQty(cart, offer);
      const required = this.getRequiredYQty(xQty, offer);
      const offerItems = this.getOfferItems(cart, offer);

      // Post message: X exists and all Y for this offer are present in required qty.
      if (xQty > 0 && required > 0 && offer.post_message) {
        const qtyByVariant = new Map();
        offerItems.forEach((item) => {
          const vid = Number(item.variant_id || 0);
          qtyByVariant.set(vid, Number(qtyByVariant.get(vid) || 0) + Number(item.quantity || 0));
        });

        const isPost = offer.product_y_variants.every((vid) => Number(qtyByVariant.get(Number(vid)) || 0) === required);
        if (isPost) {
          selected.push({
            type: 'post',
            offer,
            message: offer.post_message,
            styles: offer.post_message_styles || {}
          });
          continue;
        }
      }

      // Pre message per offer, independent from other offers.
      if (offer.pre_message && cartItemCount > 0 && xQty === 0) {
        selected.push({
          type: 'pre',
          offer,
          message: offer.pre_message,
          styles: offer.pre_message_styles || {}
        });
      }
    }

    if (!selected.length) {
      this.messagesEl.style.display = 'none';
      this.messagesEl.innerHTML = '';
      return;
    }

    this.messagesEl.style.display = 'block';
    this.messagesEl.innerHTML = selected
      .map((entry) => {
        const bg = entry.styles.background || '';
        const border = entry.styles.border || '';
        const text = entry.styles.text || '';
        const icon = entry.offer.message_icon
          ? `<img class="cart-offer-message__icon" src="${entry.offer.message_icon}" alt="" width="24" height="24" aria-hidden="true" loading="lazy">`
          : '';
        const body =
          entry.type === 'post'
            ? entry.message
            : this.escapeHtml(entry.message);

        return `
          <div class="cart-offer-message cart-offer--visible"
               style="${bg ? `background:${bg};` : ''}${border ? `border-color:${border};` : ''}${text ? `color:${text};` : ''}">
            <div class="cart-offer-message__inner">
              ${icon}
              <span class="cart-offer-message__text" style="${text ? `color:${text};` : ''}">
                ${body}
              </span>
            </div>
          </div>
        `;
      })
      .join('');
  }

  escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}

if (!customElements.get('buy-x-get-y-offers')) {
  customElements.define('buy-x-get-y-offers', BuyXGetYOffers);
}

if (!customElements.get('buy-x-get-y-offer')) {
  customElements.define('buy-x-get-y-offer', class extends HTMLElement {});
}